import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    padding: theme.spacing(1, 2, 3),
    backgroundColor: "#F8FAFC",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1.5, 2.5),
    },
  },
  paper: {
    padding: 0,
    backgroundColor: "#F0FDF4",
    borderRadius: 18,
    border: "1px solid #E2E8F0",
    boxShadow: "0 2px 10px rgba(39, 52, 73, 0.06)",
    overflow: "hidden",
  },
  chartHeader: {
    padding: "12px 16px",
    backgroundColor: "#FFFFFF",
    borderBottom: "1px solid #E2E8F0",
  },
  chartTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#273449",
    letterSpacing: "0.01em",
    margin: 0,
  },
  chartBody: {
    padding: theme.spacing(2),
  },
  legendBox: {
    width: "42%",
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
    [theme.breakpoints.up("md")]: {
      width: 150,
      marginLeft: theme.spacing(1),
    },
  },
  legendItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(0.75),
    padding: "5px 6px",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    border: "1px solid #E2E8F0",
  },
  legendColorBox: {
    width: 10,
    height: 10,
    borderRadius: 3,
    marginRight: theme.spacing(1),
    marginTop: 4,
    flexShrink: 0,
  },
  tooltipBox: {
    backgroundColor: "#FFFFFF",
    padding: theme.spacing(1),
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(39, 52, 73, 0.08)",
  },
  chartContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    gap: 4,
  },
  chartBox: {
    flex: 1,
    minWidth: 0,
    width: "58%",
    height: 200,
    minHeight: 200,
    [theme.breakpoints.up("md")]: {
      height: 250,
      minHeight: 250,
    },
  },
}));

const DashboardCharts = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [chartKey, setChartKey] = useState(0);

  // Force chart remount on breakpoint change so ResponsiveContainer gets size
  useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [isMobile]);

  // constants
  // Colors for the charts
  const dayColors = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444"];
  const foodColors = ["#EF4444", "#14B8A6", "#3B82F6", "#F59E0B"];

  const calculatePercentages = (salesData, total) => {
    if (!salesData || salesData.length === 0) return [];

    // Calculate raw percentages
    const percentages = salesData.map((item) => ({
      name: item.name,
      value: total === 0 ? 0 : (item.amount / total) * 100,
      amount: item.amount,
    }));

    // Normalize percentages to ensure their sum equals 100
    const totalPercentage = percentages.reduce(
      (acc, item) => acc + item.value,
      0
    );
    const difference = 100 - totalPercentage;

    // Adjust the last item's percentage to account for rounding errors
    if (percentages.length > 0 && total > 0) {
      percentages[percentages.length - 1].value += difference;
    }

    // Round percentages to 1 decimal place
    return percentages.map((item) => ({
      ...item,
      value: Number(item.value.toFixed(1)),
    }));
  };

  const calculateQtyPercentages = (qtyData, totalQty) => {
    if (!qtyData || qtyData.length === 0) return [];
    return qtyData.map((item) => ({
      name: item.productName,
      value:
        totalQty === 0
          ? 0
          : Number(((item.totalQty / totalQty) * 100).toFixed(1)),
      totalQty: item.totalQty,
    }));
  };

  //useState
  const salesData = [
    { name: "Restaurant", amount: (data?.totalRestSale || 0) + (data?.totalBankDiscReImbur || 0) },
    { name: "Food Panda", amount: data?.totalFPSale || 0 },
    { name: "Online", amount: data?.totalOnlineSale || 0 },
    { name: "Dastak", amount: data?.totalDastakSale || 0 },
  ];

  const saleTypeTotal = salesData.reduce((sum, item) => sum + item.amount, 0);

  //useState
  const saleAnalysisData = calculatePercentages(salesData, saleTypeTotal);
  const foodData =
    (data?.top4ProdSales || []).length > 0
      ? calculatePercentages(
          data?.top4ProdSales.map((item) => ({
            name: item.productName,
            amount: item.top4ProdSale,
          })),
          data?.totalSale || 0
        )
      : [
          { name: "Food-1", value: 0, amount: 0 },
          { name: "Food-2", value: 0, amount: 0 },
          { name: "Food-3", value: 0, amount: 0 },
          { name: "Food-4", value: 0, amount: 0 },
        ];

  //Generic
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box className={classes.tooltipBox}>
          <Typography variant="body2">
            {`${payload[0].name}: ${payload[0].value}%`}
          </Typography>
        </Box>
      );
    }
    return <></>;
  };

  const totalQty =
    data?.top4ProdSalesByQty?.reduce((acc, item) => acc + item.totalQty, 0) ||
    0;

  const foodDataByQty =
    (data?.top4ProdSalesByQty || []).length > 0
      ? calculateQtyPercentages(data?.top4ProdSalesByQty, totalQty)
      : [
          { name: "Food-1", value: 0, amount: 0 },
          { name: "Food-2", value: 0, amount: 0 },
          { name: "Food-3", value: 0, amount: 0 },
          { name: "Food-4", value: 0, amount: 0 },
        ];

  const chartHeight = isMobile ? 200 : 250;
  const innerRadius = isMobile ? 42 : 60;
  const outerRadius = isMobile ? 68 : 85;

  const renderPie = (pieData, colors) => {
    // Recharts hides pie when all values are 0 — use tiny placeholder so ring still shows
    const hasValue = pieData.some((d) => Number(d.value) > 0);
    const displayData = hasValue
      ? pieData
      : pieData.map((d, i) => (i === 0 ? { ...d, value: 1 } : d));

    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <PieChart>
          <Pie
            data={displayData}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
            isAnimationActive={!isMobile}
          >
            {displayData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  hasValue
                    ? colors[index % colors.length]
                    : "#e0e0e0"
                }
              />
            ))}
          </Pie>
          {hasValue && <Tooltip content={<CustomTooltip />} />}
        </PieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box className={classes.pageWrap}>
      <Grid container spacing={2}>
        {/* Sales Analysis by saleType */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} className={classes.paper}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                Sales Analysis by SaleType
              </Typography>
            </Box>
            <Box className={classes.chartBody}>
              <Box className={classes.chartContainer}>
                <Box className={classes.chartBox} key={`sale-${chartKey}`}>
                  {renderPie(saleAnalysisData, dayColors)}
                </Box>
                <Box className={classes.legendBox}>
                  {salesData.map((item, index) => (
                    <Box key={index} className={classes.legendItem}>
                      <Box
                        className={classes.legendColorBox}
                        style={{
                          backgroundColor: dayColors[index % dayColors.length],
                        }}
                      />
                      <Typography variant="body2" style={{ fontSize: "0.78rem", color: "#64748B" }}>
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            color: "#273449",
                          }}
                        >
                          {item.name}{" "}
                        </Typography>
                        {item.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        ({Math.round(saleAnalysisData[index]?.value || 0)}%)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Popular Foods Chart by QTY */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} className={classes.paper}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                Most Popular Food
              </Typography>
            </Box>
            <Box className={classes.chartBody}>
              <Box className={classes.chartContainer}>
                <Box className={classes.chartBox} key={`qty-${chartKey}`}>
                  {renderPie(foodDataByQty, foodColors)}
                </Box>
                <Box className={classes.legendBox}>
                  {foodDataByQty.map((item, index) => (
                    <Box key={index} className={classes.legendItem}>
                      <Box
                        className={classes.legendColorBox}
                        style={{
                          backgroundColor: foodColors[index % foodColors.length],
                        }}
                      />
                      <Typography variant="body2" style={{ fontSize: "0.78rem", color: "#64748B" }}>
                        <Typography
                          style={{ fontWeight: 700, fontSize: "0.78rem", color: "#273449" }}
                          variant="body2"
                        >
                          {item.name}
                        </Typography>{" "}
                        {item.totalQty
                          ? Math.round(item.totalQty).toFixed(2)
                          : "0.00"}{" "}
                        ({Math.round(item.value)}%)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Popular Foods Chart by Sales Amount */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} className={classes.paper}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                Top Food By Sales
              </Typography>
            </Box>
            <Box className={classes.chartBody}>
              <Box className={classes.chartContainer}>
                <Box className={classes.chartBox} key={`food-${chartKey}`}>
                  {renderPie(foodData, foodColors)}
                </Box>
                <Box className={classes.legendBox}>
                  {foodData.map((item, index) => (
                    <Box key={index} className={classes.legendItem}>
                      <Box
                        className={classes.legendColorBox}
                        style={{
                          backgroundColor: foodColors[index % foodColors.length],
                        }}
                      />
                      <Typography variant="body2" style={{ fontSize: "0.78rem", color: "#64748B" }}>
                        <Typography
                          style={{ fontWeight: 700, fontSize: "0.78rem", color: "#273449" }}
                          variant="body2"
                        >
                          {item.name}
                        </Typography>{" "}
                        {item.amount
                          ? Math.round(item.amount).toFixed(2)
                          : "0.00"}{" "}
                        ({Math.round(item.value)}%)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardCharts;
